import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { ApiError, apiClient } from "@/api/client";
import { queryKeys } from "@/api/hooks";
import type { CurrentUserResponse } from "@/api/types";
import { useOptionalClerk } from "@/lib/clerk";
import { TERMS_VERSION } from "@/routes/legal/TermsPage";

/** Soft-prompt modal that fires when the authenticated user's
 *  ``terms_accept_required`` flag is true (server computes by
 *  comparing ``user.terms_accepted_version`` to the current
 *  ``CURRENT_TERMS_VERSION``).
 *
 *  Renders unconditionally on top of whatever else is mounted —
 *  blocks interaction until the user clicks Accept. Decline is
 *  represented as Sign Out (we can't keep them in a session
 *  whose terms acceptance is permanently stale; offering "skip"
 *  defeats the purpose).
 *
 *  The Layout component renders this; this file is the modal
 *  itself + the mutation. */
export function TermsReacceptModal() {
  const qc = useQueryClient();
  const accept = useMutation<CurrentUserResponse, ApiError, void>({
    mutationFn: () =>
      apiClient.post<CurrentUserResponse>("/auth/accept-terms", {
        version: TERMS_VERSION,
      }),
    onSuccess: (fresh) => {
      qc.setQueryData(queryKeys.me, fresh);
    },
  });

  const clerk = useOptionalClerk();
  const signOut = useMutation<void, ApiError, void>({
    mutationFn: async () => {
      // Both halves — legacy session + Clerk — same as useLogout.
      await apiClient.post("/auth/logout");
      await clerk?.signOut();
    },
    onSuccess: () => {
      qc.clear();
      window.location.href = "/login";
    },
  });

  return (
    // biome-ignore lint/a11y/useSemanticElements: custom modal pattern; native <dialog> has different semantics (showModal/close lifecycle, top-layer rendering) that this component doesn't use.
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-reaccept-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-graphite/40 px-6 py-12"
    >
      <div className="w-full max-w-md rounded-lg border border-rule bg-paper-warm shadow-lg">
        <div className="px-5 py-5">
          <h2
            id="terms-reaccept-title"
            className="font-mono text-base font-bold tracking-tight text-graphite"
          >
            Updated Terms of Service
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-graphite-soft">
            We've updated PencilOut's Terms of Service and Privacy Policy. To keep using your
            account, please review and accept the latest version.
          </p>
          <div className="mt-3 flex gap-3 text-xs text-graphite-soft">
            <Link
              to="/terms"
              target="_blank"
              className="text-graphite underline hover:no-underline"
            >
              Read updated Terms
            </Link>
            <Link
              to="/privacy"
              target="_blank"
              className="text-graphite underline hover:no-underline"
            >
              Read Privacy Policy
            </Link>
          </div>

          {accept.error && (
            <p className="mt-3 text-sm text-red-pencil">
              {accept.error instanceof ApiError
                ? accept.error.detail
                : "Couldn't record your acceptance. Try again."}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-rule bg-paper px-5 py-3">
          <button
            type="button"
            onClick={() => signOut.mutate()}
            disabled={signOut.isPending || accept.isPending}
            className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft transition hover:text-graphite disabled:opacity-50"
          >
            {signOut.isPending ? "Signing out…" : "Sign out"}
          </button>
          <button
            type="button"
            onClick={() => accept.mutate()}
            disabled={accept.isPending || signOut.isPending}
            className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
          >
            {accept.isPending ? "Working…" : "I accept"}
          </button>
        </div>
      </div>
    </div>
  );
}
